import type { Txn } from './transactionWire'

/**
 * `at` is an ISO instant the screen formats as a DATE; null when the source has none to give.
 * `rule`, on the category entry only, names the rule that set it ("Auto-rule “X” set the
 * category"); `ruleId` is null once that rule is deleted, so the screen does not link it.
 */
export type ActivityEntry = { key: string; text: string; at: string | null; rule?: { name: string; ruleId: string | null } }

const CATEGORY_TEXT = {
  rule: 'Category set by an auto-rule',
  ai: 'Category suggested automatically',
  provider: 'Category from your bank',
  user: 'Category set by you',
  memory: 'Auto · from your edits',
} as const

/**
 * The reduced activity log (Q11), newest first. The API has no audit trail, so
 * this is what the row itself records: `reviewed_at`, `category_source` (which
 * says WHO set the category) and how it arrived. Which rule set it comes from
 * the rule's log, read by the screen and passed as `categoryRule`: given only
 * when the row says a rule set it and the log names one. Never a person or a
 * time of day — neither is on the wire. `memory` is the server's learned per-user memory
 * (backend #525); its label is the contract's own words.
 */
export function activityLog(
  t: Pick<Txn, 'reviewedAt' | 'categorySource' | 'provider' | 'createdAt'>,
  categoryRule: { name: string; ruleId: string | null } | null = null,
): ActivityEntry[] {
  const entries: ActivityEntry[] = []
  if (t.reviewedAt !== null) entries.push({ key: 'reviewed', text: 'Reviewed', at: t.reviewedAt })
  if (t.categorySource === 'rule' && categoryRule !== null) {
    entries.push({ key: 'category', text: `Auto-rule “${categoryRule.name}” set the category`, at: null, rule: categoryRule })
  } else if (t.categorySource !== null && t.categorySource !== 'none') {
    entries.push({ key: 'category', text: CATEGORY_TEXT[t.categorySource], at: null })
  }
  const synced = t.provider !== null && t.provider !== 'manual'
  entries.push({ key: 'added', text: synced ? 'Added from bank sync' : 'Added manually', at: t.createdAt })
  return entries
}
